<template>
  <div>
    <div class="d-flex flex-wrap" style="max-width: 580px">
      <v-text-field
        v-model="search"
        class="fix-google-chrome mr-2"
        label="รหัส - ชื่อ Device"
        prepend-inner-icon="mdi-magnify"
        clearable
        outlined
        dense
        hide-details
        @input="handleSearch"
      ></v-text-field>
      <v-autocomplete
        v-model="filterArea"
        class="fix-google-chrome"
        label="พื้นที่"
        :items="areas"
        item-text="title_th"
        item-value="area_uuid"
        outlined
        dense
        hide-details
        clearable
        @change="handleFilterArea"
        :loading="loadingFilterArea"
      >
      </v-autocomplete>
      <v-btn class="ml-2" icon :loading="loading" @click="handleRefresh">
        <v-icon>mdi-refresh</v-icon>
      </v-btn>
    </div>
    <v-data-table
      class="mt-4 with-borders"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      :loading="loading"
      :headers="headers"
      :items="flattenedRows"
      :options.sync="optionsHardware"
      :server-items-length="authorityHardwareInAuthority.total"
      :items-per-page="10"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 50, 100],
        itemsPerPageText: 'Rows per page:',
      }"
      must-sort
    >
      <template v-slot:body="{ items }">
        <tbody>
          <tr v-if="!items.length">
            <td style="text-align: center" colspan="4">No data available</td>
          </tr>
          <tr v-for="(item, index) in items" :key="index">
            <!-- Hardware -->
            <td
              :style="`vertical-align: ${
                item.hardwareRowSpan > 1 ? 'top' : 'middle'
              }; padding: 10px 15px`"
              v-if="item.showHardware"
              :rowspan="item.hardwareRowSpan"
            >
              {{ item.hardwareCode }} - {{ item.hardwareTitle }}
            </td>

            <!-- Authority -->
            <td>
              <div class="d-flex justify-space-between align-center">
                <div>
                  <div>{{ item.authority }}</div>
                  <div class="caption grey--text">
                    <span v-if="item.accessControlStartTime">
                      {{ item.accessControlStartTime }}
                    </span>
                    <span v-if="item.accessControlEndTime">
                      {{ `- ${item.accessControlEndTime}` }}
                    </span>
                  </div>
                </div>
                <div>
                  <v-btn
                    text
                    color="error"
                    @click.stop="onDeleteDevice(item)"
                    :disabled="
                      uiCtrl &&
                      !uiCtrl.isEnable('btn:::authority_employee:delete')
                    "
                    >ลบ</v-btn
                  >
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </template>
    </v-data-table>
    <ConfirmDialog
      :dialog="confirmDeleteDevice"
      :loading="confirmDeleteDeviceLoading"
      :dialogData="confirmDeleteDeviceData"
      @cancelConfirmDialog="cancelConfirmDeleteDevice"
      @submitConfirmDialog="submitConfirmDeleteDevice"
    />
  </div>
</template>

<script>
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'

export default {
  components: {
    ConfirmDialog,
  },
  props: {
    dialogCheckPermissions: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      search: '',
      filterArea: null,
      sortBy: ['hardwareCode'],
      sortDesc: [false],
      loading: false,
      loadingFilterArea: false,
      headers: [
        {
          text: 'Device',
          align: 'start',
          value: 'hardwareCode',
          width: '20%',
        },
        {
          text: 'กลุ่มสิทธิ์',
          value: 'authority',
          width: '80%',
          sortable: false,
        },
      ],
      optionsHardware: {},
      confirmDeleteDevice: false,
      confirmDeleteDeviceLoading: false,
      confirmDeleteDeviceData: {},
    }
  },
  computed: {
    uiCtrl() {
      return this.$store.state.uiCtrl
    },
    areas() {
      return this.$store.state.area.areas
    },
    authorityHardwareInAuthority() {
      return this.$store.state.accessAuthority.authorityHardwareInAuthority
    },
    flattenedRows() {
      const result = []
      const hardwares = this.authorityHardwareInAuthority.hardwares || []

      hardwares.forEach((hardware) => {
        const { hardware_uuid, hardware_code, title_th } = hardware
        const authorities = hardware.authorities || []
        const hardwareRowSpan = authorities.length || 1

        if (authorities.length === 0) {
          result.push({
            hardwareCode: hardware_code,
            hardwareTitle: title_th,
            showHardware: true,
            hardwareRowSpan: 1,
            authority: '',
            authority_uuid: null,
            accessControlStartTime: '',
            accessControlEndTime: '',
          })
          return
        }

        authorities.forEach((auth, authIndex) => {
          result.push({
            hardware_uuid,
            hardwareCode: hardware_code,
            hardwareTitle: title_th,
            showHardware: authIndex === 0,
            hardwareRowSpan: authIndex === 0 ? hardwareRowSpan : 0,
            authority: `${auth.authority_code} - ${auth.title_th}`,
            authority_uuid: auth.authority_uuid,
            accessControlStartTime: auth.access_control_start_time
              ? moment
                  .utc(auth.access_control_start_time)
                  .format('DD/MM/YYYY HH:mm')
              : '',
            accessControlEndTime: auth.access_control_end_time
              ? moment
                  .utc(auth.access_control_end_time)
                  .format('DD/MM/YYYY HH:mm')
              : '',
          })
        })
      })

      return result
    },
  },
  watch: {
    async dialogCheckPermissions(value) {
      if (value) {
        await this.getAuthorityHardwareInAuthority()
      }
    },
    optionsHardware: {
      async handler() {
        await this.getAuthorityHardwareInAuthority()
      },
      deep: true,
    },
  },
  async created() {
    try {
      const { company_uuid } = this.$store.state.authentication.company

      this.loadingFilterArea = true

      const payload = {
        company_uuid,
        params: {
          dpc: 'access-authority-summary-to-list-area',
        },
      }

      await this.fetchAreas(payload)
    } catch (error) {
      const snackbar = {
        show: true,
        color: 'error',
        timeout: -1,
        message: error?.response?.data?.message || 'Something went wrong!',
      }
      this.$store.commit('SET_SNACKBAR', snackbar)
    } finally {
      this.loadingFilterArea = false
    }
  },
  methods: {
    ...mapActions({
      fetchAuthorityHardwareInAuthority:
        'accessAuthority/fetchAuthorityHardwareInAuthority',
      fetchAreas: 'area/fetchAreas',
      deleteAuthorityDeviceMember:
        'accessAuthority/deleteAuthorityDeviceMember',
      fetchAccessAuthority: 'accessAuthority/fetchAccessAuthority',
    }),
    onDeleteDevice(item) {
      this.confirmDeleteDevice = true
      this.confirmDeleteDeviceData = {
        title: 'ยืนยันการลบ',
        text: `คุณต้องการลบ Device`,
        value: `${item.hardwareCode} - ${item.hardwareTitle} จากกลุ่ม ${item.authority}`,
        item: { data: { ...item } },
      }
    },
    cancelConfirmDeleteDevice() {
      this.confirmDeleteDevice = false
    },
    async submitConfirmDeleteDevice({ data }) {
      try {
        const { company_uuid } = this.$store.state.authentication.company

        this.confirmDeleteDeviceLoading = true

        await this.deleteAuthorityDeviceMember({
          company_uuid,
          authority_uuid: data.authority_uuid,
          body: {
            hardware_uuid: data.hardware_uuid,
          },
        })

        const accessAuthority = await this.fetchAccessAuthority({
          company_uuid,
          authority_uuid: data.authority_uuid,
        })

        this.$store.commit(
          'accessAuthority/EDIT_ACCESS_AUTHORITY',
          accessAuthority
        )

        this.optionsHardware.page = 1
        this.getAuthorityHardwareInAuthority()

        const snackbar = {
          show: true,
          color: 'success',
          timeout: 4000,
          message: 'ลบข้อมูลสำเร็จ',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } finally {
        this.confirmDeleteDeviceLoading = false
        this.confirmDeleteDevice = false
      }
    },
    handleRefresh() {
      this.optionsHardware.page = 1
      this.getAuthorityHardwareInAuthority()
    },
    handleSearch() {
      this.loading = true
      this.getAuthorityHardwareInAuthorityBySearch()
    },
    handleFilterArea() {
      this.optionsHardware.page = 1
      this.getAuthorityHardwareInAuthority()
    },
    getAuthorityHardwareInAuthorityBySearch: _.debounce(function () {
      this.optionsHardware.page = 1
      this.getAuthorityHardwareInAuthority()
    }, 1000),
    async getAuthorityHardwareInAuthority() {
      try {
        this.loading = true

        const { sortBy, sortDesc, page, itemsPerPage } = this.optionsHardware

        let sort = ''
        const sortByKey = sortBy[0] || ''

        if (sortByKey === 'hardwareCode') sort = 'hardware_code'

        if (sortDesc.length > 0) {
          if (sortDesc[0]) {
            sort += `,desc`
          } else {
            sort += `,asc`
          }
        }

        const { company_uuid } = this.$store.state.authentication.company

        const payload = {
          company_uuid,
          params: {
            // dpc: '',
            search: this.search || undefined,
            area_uuid: this.filterArea || undefined,
            sort,
            limit: `${itemsPerPage},${(page - 1) * itemsPerPage}`,
          },
        }

        await this.fetchAuthorityHardwareInAuthority(payload)
      } catch (error) {
        const snackbar = {
          show: true,
          color: 'error',
          timeout: -1,
          message: error?.response?.data?.message || 'Something went wrong!',
        }
        this.$store.commit('SET_SNACKBAR', snackbar)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.with-borders >>> table,
.with-borders >>> th,
.with-borders >>> td {
  border: 1px solid #ccc;
  border-collapse: collapse;
}

.with-borders >>> th,
.with-borders >>> td {
  padding: 8px;
}
</style>
